import { ImageWidth } from 'common/helpers/image'

import { createRenderTarget } from 'component-library/render-target'
import { AmpPicture } from 'component-library/content/Picture/Picture.amp'
import { WebPicture } from 'component-library/content/Picture/Picture.web'
import { ImageSource, BreakpointRatios, FixedRatio } from 'common/client-data-types/image-item'
import { SimpleImg } from './SimpleImg'

export interface CommonProps {
    className?: string
    disableLazyLoad?: boolean
    loadingBackgroundColor?: string
    imageWidths?: ImageWidth
    /** ImageSizes is ignored on RenderTarget = AMP as most images will be near to width of the viewport */
    imageSizes?: string
    renderPicture?: (pic: React.ReactElement) => React.ReactElement
}

export interface ObjectFitContain extends CommonProps {
    /**
     * Emulates object-fit: contain using the image dimensions
     *
     * If no dimensions present will fail to render
     *
     * NOTE: This is what google AMP expects
     */
    layout: 'object-fit-contain'
    image: ImageSource
    fixedRatio?: FixedRatio | BreakpointRatios
    /** Use original crop in a fixed ratio image placeholder */
    useOriginalCrop?: boolean
}

export interface FillHorizonally extends CommonProps {
    /**
     * When no dimensions or fixedRatio specified
     * nothing will happen until the image loads, where
     * it will fill the available horizontal space
     */
    layout: 'fill-container-horizontally'
    fixedRatio?: FixedRatio | BreakpointRatios
    image: ImageSource
}

/**
 * Covers the container, clipping to fit the aspect ratio
 */
export interface ObjectFitCover extends CommonProps {
    /**
     * Covers the container, clipping to fit the aspect ratio
     */
    layout: 'object-fit-cover'
    fixedRatio: FixedRatio | BreakpointRatios
    /** Use original crop in a fixed ratio image placeholder */
    useOriginalCrop?: boolean
    image: ImageSource
}

/** Picture component with minimum styles */
export interface None extends CommonProps {
    /**
     * When src is undefined, you must have a fixed ratio to
     * show the placeholder.
     */
    layout: 'none'
    image: ImageSource
    fixedRatio: FixedRatio | BreakpointRatios | undefined
}

export interface Placeholder extends CommonProps {
    /**
     * When src is undefined, you must have a fixed ratio to
     * show the placeholder.
     */
    layout: 'placeholder'
    image?: undefined
    fixedRatio: FixedRatio | BreakpointRatios
}

export type Props = ObjectFitContain | Placeholder | FillHorizonally | ObjectFitCover | None

export const Picture = createRenderTarget<Props>('Picture', {
    amp: AmpPicture,
    web: WebPicture,
    rss: SimpleImg,
    preview: WebPicture,
})
